import React from 'react';
import { append, update } from 'ramda';
import { convertUrval } from '../SavedUrval/Utils/convertSavedUrval';
import { getNewUrval } from '../../Utils/urvalUtils';
import {
  IUIUrval,
  NodeUrvalType,
  IUIPage,
  RutinUrvalType,
  TEDUrvalVisibility,
} from '../../../../Types/UrvalTypes';
import ModifyUrvalMutation from './Components/ModifyUrval/Mutations/ModifyUrvalMutation';
import SaveNewUrvalMutation, {
  SaveNewUrvalMutationUpdaterType,
} from './Components/SaveNewUrval/Mutations/SaveNewUrvalMutation';
import ActiveDatabaseName from '../../../../Features/Routing/ActiveDatabaseName';
import UrvalToolbarQuery from './Queries/UrvalToolbarQuery';
import SaveNewUrvalDialog from './Components/SaveNewUrval/Components/SaveNewUrvalDialog';
import ModifyUrvalDialog from './Components/ModifyUrval/Components/ModifyUrvalDialog';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import UrvalToolbarMenu from './Components/UrvalToolbarMenu/UrvalToolbarMenu';
import { ModelContext } from '../../../../Model/ModelContext/ModelContext';
import { UrvalFieldSortModeType } from '../UrvalFieldPicker/Types/UrvalFieldPickerTypes';
import { TEDSelectionVisibility } from '../../../../globalQueryTypes';
import { RutinUrvalListQueryVariables } from '../SavedUrval/Queries/RutinUrvalListQuery/_QueryTypes/RutinUrvalListQuery';
import { RemoveUrvalMutationUpdaterType } from '../SavedUrval/Mutations/RemoveUrvalMutation';
import { ActiveRoutineContext } from '../../../../Features/Routing/ActiveRoutine/ActiveRoutineContext';

interface IProps {
  setUrval: (newUrval: IUIUrval) => void;
  setTab: (tab: number) => void;
  collectionName: string;
  rutinUrvalList?: RutinUrvalType[] | null;
  rutinUrvalVariables?: RutinUrvalListQueryVariables;
  addNewPage: () => void;
  pickedSavedUrval?: NodeUrvalType;
  routineID?: number;
  codeSystemID?: string;
  urval: IUIUrval;
  openFieldPickerWithAllExpandableOpen?: boolean;
  fieldSortMode?: UrvalFieldSortModeType;
  kodplanUrval: boolean;
  saveUrvalAvailableVisibilityOptions?: TEDUrvalVisibility[];
  saveNewUrvalMutationUpdater?: SaveNewUrvalMutationUpdaterType;
  removeUrvalMutationUpdater?: RemoveUrvalMutationUpdaterType;
  saveNewUrvalDialogOpen: boolean;
  setSaveNewUrvalDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addTab: () => void;
  fieldPickerDialogOpen: boolean;
  toggleFieldPickerDialogOpen: () => void;
  classes?: any;
}

interface IState {
  modifyUrvalDialogOpen: boolean;
  /** If tools menu is open, tools menu is only visible on smaller screen sizes */
  toolsMenuOpen: boolean;
}

const styles = (theme: Theme) => ({});

/**
 * Toolbar för att ställa in ett Urval
 */
export class UrvalToolbar extends React.Component<IProps, IState> {
  static contextType = ModelContext;
  declare context: React.ContextType<typeof ModelContext>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      modifyUrvalDialogOpen: false,
      toolsMenuOpen: false,
    };
  }

  setUrval = (urval: IUIUrval) => {
    this.props.setTab(0);
    this.props.setUrval(urval);
  };

  newUrval = () => {
    this.setUrval(
      getNewUrval(this.context.avenyCollectionMap, this.props.collectionName)
    );
  };

  setFirstPage = (newUrvalPage: IUIPage) => {
    const { urval, setUrval } = this.props;
    const firstPage = urval.pages[0];

    const newPages =
      firstPage == null
        ? append(newUrvalPage, urval.pages)
        : update(0, newUrvalPage, urval.pages);
    const newUrval: IUIUrval = { ...urval, pages: newPages };
    setUrval(newUrval);
  };

  resetPickedSavedUrval = () => {
    if (this.props.pickedSavedUrval) {
      this.setUrval(
        convertUrval(
          this.props.pickedSavedUrval,
          this.props.collectionName,
          this.context.avenyCollectionMap
        )
      );
    }
  };

  onResetUrvalClick = () => {
    this.setState({ toolsMenuOpen: false });
    this.resetPickedSavedUrval();
  };

  onNewSelectionClick = () => {
    this.newUrval();
    this.setState({ toolsMenuOpen: false });
    this.props.toggleFieldPickerDialogOpen();
  };

  onModifyUrvalClick = () => {
    this.setState({ modifyUrvalDialogOpen: true, toolsMenuOpen: false });
  };

  onSaveNewUrvalClick = () => {
    this.props.setSaveNewUrvalDialogOpen(true);
    this.setState({ toolsMenuOpen: false });
  };

  onToggleFavoritClick = (toggleFavorit: () => void) => () => {
    this.setState({ toolsMenuOpen: false });
    toggleFavorit();
  };

  closeModifyUrvalDialog = () => {
    this.setState({ modifyUrvalDialogOpen: false });
  };

  closeSaveNewUrvalDialog = () => {
    this.props.setSaveNewUrvalDialogOpen(false);
  };

  closeFieldPickerDialog = () => {
    this.props.toggleFieldPickerDialogOpen();
  };

  toggleToolsMenu = () => {
    this.setState(prevState => ({ toolsMenuOpen: !prevState.toolsMenuOpen }));
  };

  closeToolsMenu = () => {
    this.setState({ toolsMenuOpen: false });
  };

  render() {
    return (
      <ActiveDatabaseName>
        {(dbName: string | null) => {
          if (dbName == null) {
            return <>Could not find database name</>;
          } else {
            return (
              <UrvalToolbarQuery
                dbName={dbName}
                collectionName={this.props.collectionName}
              >
                {data => {
                  const me = data.me && data.me[0];
                  const userId =
                    me && me.userId != null ? me.userId : undefined;

                  const canSaveGemensammaUrval = data.me
                    ? data.me[0].delsystemAnsvarig.value
                    : false;

                  const saveUrvalAvailableVisibilityOptions: TEDUrvalVisibility[] =
                    this.props.saveUrvalAvailableVisibilityOptions
                      ? this.props.saveUrvalAvailableVisibilityOptions.filter(
                          visibilityOption =>
                            canSaveGemensammaUrval ||
                            visibilityOption !==
                              TEDSelectionVisibility.gemensamt
                        )
                      : canSaveGemensammaUrval
                      ? [
                          TEDSelectionVisibility.privat,
                          TEDSelectionVisibility.gemensamt,
                        ]
                      : [TEDSelectionVisibility.privat];

                  const usageCode = this.props.rutinUrvalVariables
                    ? this.props.rutinUrvalVariables.usagecode || undefined
                    : undefined;

                  return (
                    <ActiveRoutineContext.Consumer>
                      {activeRoutine => {
                        const rutinID =
                          this.props.routineID != null
                            ? this.props.routineID
                            : activeRoutine?.rutinInfo.rutinID;
                        if (rutinID == null) {
                          return 'Could not get Routine Info';
                        } else {
                          return (
                            <ModifyUrvalMutation
                              setUrval={this.props.setUrval}
                              collectionName={this.props.collectionName}
                            >
                              {modifyUrval => (
                                <SaveNewUrvalMutation
                                  dbName={dbName}
                                  setUrval={this.props.setUrval}
                                  collectionName={this.props.collectionName}
                                  rutinID={rutinID}
                                  kodplanUrval={this.props.kodplanUrval}
                                  usagecode={usageCode}
                                  saveNewUrvalMutationUpdater={
                                    this.props.saveNewUrvalMutationUpdater
                                  }
                                >
                                  {saveSelection => (
                                    <React.Fragment>
                                      <UrvalToolbarMenu
                                        hasPickedSavedUrval={
                                          this.props.pickedSavedUrval != null
                                        }
                                        onResetUrvalClick={
                                          this.onResetUrvalClick
                                        }
                                        onNewSelectionClick={
                                          this.onNewSelectionClick
                                        }
                                        onModifyUrvalClick={
                                          this.onModifyUrvalClick
                                        }
                                        onSaveNewUrvalClick={
                                          this.onSaveNewUrvalClick
                                        }
                                        isGemensamtUrval={
                                          this.props.urval.savedUrvalProps
                                            ? this.props.urval.savedUrvalProps
                                                .synlighet ===
                                              TEDSelectionVisibility.gemensamt
                                            : false
                                        }
                                        canSaveGemensammaUrval={
                                          canSaveGemensammaUrval
                                        }
                                        availableVisibilityOptions={
                                          saveUrvalAvailableVisibilityOptions
                                        }
                                        addTab={this.props.addTab}
                                      />
                                      <ModifyUrvalDialog
                                        title="Spara"
                                        modifyUrval={modifyUrval}
                                        urval={this.props.urval}
                                        collectionName={
                                          this.props.collectionName
                                        }
                                        kodplanId={
                                          this.props.codeSystemID || ''
                                        }
                                        userId={userId}
                                        rutinId={rutinID}
                                        open={this.state.modifyUrvalDialogOpen}
                                        onClose={this.closeModifyUrvalDialog}
                                        usageCode={usageCode}
                                        availableVisibilityOptions={
                                          saveUrvalAvailableVisibilityOptions
                                        }
                                      />
                                      <SaveNewUrvalDialog
                                        title="Spara som"
                                        saveUrval={saveSelection}
                                        urval={this.props.urval}
                                        collectionName={
                                          this.props.collectionName
                                        }
                                        userId={userId}
                                        rutinId={rutinID}
                                        open={this.props.saveNewUrvalDialogOpen}
                                        onClose={this.closeSaveNewUrvalDialog}
                                        usageCode={usageCode}
                                        availableVisibilityOptions={
                                          saveUrvalAvailableVisibilityOptions
                                        }
                                      />
                                    </React.Fragment>
                                  )}
                                </SaveNewUrvalMutation>
                              )}
                            </ModifyUrvalMutation>
                          );
                        }
                      }}
                    </ActiveRoutineContext.Consumer>
                  );
                }}
              </UrvalToolbarQuery>
            );
          }
        }}
      </ActiveDatabaseName>
    );
  }
}

export default withStyles(UrvalToolbar, styles);
